import { Button, Dialog, Message } from '@alifd/next';
import { Box, Typography } from '@mui/material';
import {
  DatePicker,
  Form,
  FormListFieldData,
  FormProps,
  Input,
  InputNumber,
  Select,
  Space,
  Table,
  TableProps,
} from 'antd';
import locale from 'antd/es/date-picker/locale/zh_TW';
import axios from 'axios';
import moment from 'moment';
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import Swal from 'sweetalert2';

import Finance from '@/types/database/finance';

import { FinanceDataContext } from '../../..';

const financeLabels = {
  paymentPeriod: '帳單期數',
  householdNumber: '戶號',
  title: '明細標題',
  subjectId: '帳單科目',
  creditSubjectId: '減免科目',
  money: '金額',
  remark: '備註',
};

interface FinanceFormValues {
  paymentPeriod: moment.Moment;
  householdNumber: string;
  title: string;
  subjectId: string;
  creditSubjectId?: string;
  money: number;
  remark?: string;
}

const { Item, useForm } = Form;

const FinanceListDialog: React.FC<{
  payTitle: string;
  // financeList: Finance[];
  setTrigger: Dispatch<SetStateAction<Record<string, never>>>;
  financeId: string;
}> = ({ payTitle, setTrigger, financeId }) => {
  const [financeList, setFinanceList] = useState([]);
  const [currentData, setCurrentData] = useState<Finance>();
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<'table' | 'display' | 'edit'>('table');
  const [showCreditSubject, setShowCreditSubject] = useState(false);
  const [subjectOptions, setSubjectOptions] = useState<
    {
      value: string;
      label: string;
    }[]
  >([]);
  const [householdNumberOptions, setHouseholdNumberOptions] = useState<
    {
      value: string;
      label: string;
    }[]
  >([]);

  const { accountingSubjects, householdNumbers } =
    useContext(FinanceDataContext);

  const [form] = useForm<FinanceFormValues>();

  useEffect(() => {
    const options = accountingSubjects.map((subject) => ({
      value: subject._id,
      label: subject.subjectName,
    }));
    setSubjectOptions(options);
  }, [accountingSubjects]);

  useEffect(() => {
    const options = householdNumbers
      .sort((a, b) => a.localeCompare(b))
      .map((number) => ({
        value: number,
        label: number,
      }));
    setHouseholdNumberOptions(options);
  }, [householdNumbers]);

  useEffect(() => {
    if (currentData) {
      form.setFieldsValue({
        ...currentData,
        paymentPeriod: moment(currentData.paymentPeriod),
      });
      if (currentData.creditSubjectId) {
        setShowCreditSubject(true);
      } else {
        setShowCreditSubject(false);
      }
    }
  }, [currentData, form]);

  const handleFinish: FormProps['onFinish'] = useCallback(
    async (values: FinanceFormValues) => {
      const body = {
        ...values,
        creditSubjectId: values.creditSubjectId || null,
        paymentPeriod: values.paymentPeriod.endOf('month').valueOf(),
      };
      try {
        await axios.patch(`/ezcon/api/bill/finance`, body, {
          headers: {
            authorization: localStorage.getItem('feathers-jwt'),
          },
          params: {
            id: currentData?._id,
          },
        });
        Message.success('修改成功');
        setTrigger({});
        setStatus('table');
        setCurrentData(undefined);
      } catch (err) {
        console.error(err);
        Message.error('修改失敗');
      }
    },
    [currentData?._id, setTrigger]
  );

  const handleFieldChange: FormProps['onFieldsChange'] = useCallback(
    (changedFields: FormListFieldData[]) => {
      if (changedFields.find((field) => field.name[0] === 'subjectId')) {
        const currentSubject = accountingSubjects.find(
          (subject) => subject._id === form.getFieldValue('subjectId')
        );
        if (currentSubject?.subjectName.match(/減免/)) {
          setShowCreditSubject(true);
        } else {
          setShowCreditSubject(false);
        }
      }
    },
    [accountingSubjects, form]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      const result = await Swal.fire({
        title: '確定要刪除嗎？',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '確定',
        cancelButtonText: '取消',
      });
      if (result.isConfirmed) {
        try {
          await axios.delete('/ezcon/api/bill/finance', {
            params: {
              id,
            },
            headers: {
              authorization: localStorage.getItem('feathers-jwt'),
            },
          });
          setTrigger({});
          Message.success('刪除成功');
        } catch (err) {
          console.error(err);
        }
      }
    },
    [setTrigger]
  );

  const columns: TableProps<Finance>['columns'] = [
    {
      title: '建立時間',
      dataIndex: 'createdTime',
      render: (time: number) => moment(time).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '明細標題',
      dataIndex: 'title',
    },
    {
      title: '金額',
      dataIndex: 'money',
    },
    {
      title: '操作',
      render: (_, finance) => (
        <Space>
          <Button
            onClick={() => {
              setCurrentData(finance);
              setStatus('display');
            }}
          >
            查看
          </Button>
          {moment(finance.paymentPeriod).isAfter(moment().endOf('month')) && (
            <>
              <Button
                type="primary"
                onClick={() => {
                  setCurrentData(finance);
                  setStatus('edit');
                }}
              >
                編輯
              </Button>
              <Button
                warning
                onClick={() => finance._id && handleDelete(finance._id)}
              >
                刪除
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  const handleFinanceDetail = async (id) => {
    try {
      const res = await axios.get(`/ezcon/api/bills/${id}`, {
        headers: {
          authorization: localStorage.getItem('feathers-jwt'),
        },
      });
      setFinanceList(res.data);
    } catch (err) {
      console.error(err);
    }
  };
  return (
    <>
      <Dialog
        visible={visible}
        onClose={() => setVisible(false)}
        title={currentData?.title || payTitle}
        footer={false}
      >
        <Box
          sx={{
            width: '600px',
            height: '600px',
            overflow: 'auto',
          }}
        >
          {status === 'table' && (
            <Table
              columns={columns}
              dataSource={financeList.map((finance) => ({
                ...finance,
                key: finance._id,
              }))}
              pagination={false}
              size="small"
            />
          )}
          {status === 'display' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                height: '100%',
                justifyContent: 'space-between',
              }}
            >
              {currentData && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                  }}
                >
                  {Object.entries(financeLabels).map(([name, label], index) => {
                    const value = currentData[name];
                    const contents = {
                      paymentPeriod: moment(Number(value || '')).format(
                        'YYYY-MM'
                      ),
                      householdNumber: value,
                      title: value,
                      subjectId:
                        accountingSubjects.find(
                          (subject) => subject._id === value
                        )?.subjectName || '無明細科目',
                      creditSubjectId:
                        accountingSubjects.find(
                          (subject) => subject._id === value
                        )?.subjectName || '無減免科目',
                      money: value,
                      remark: value || '無備註',
                    };

                    if (currentData[name]) {
                      return (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <Typography variant="body1">{label}</Typography>
                          <Typography variant="body2">
                            {contents[name]}
                          </Typography>
                        </Box>
                      );
                    } else {
                      return null;
                    }
                  })}
                </Box>
              )}
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  onClick={() => {
                    setStatus('table');
                    setCurrentData(undefined);
                  }}
                >
                  返回
                </Button>
              </Box>
            </Box>
          )}
          {status === 'edit' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%',
              }}
            >
              <Form
                form={form}
                onFinish={handleFinish}
                onFieldsChange={handleFieldChange}
              >
                <Item
                  name="paymentPeriod"
                  label="明細期數"
                  rules={[
                    {
                      required: true,
                      message: '請選擇明細期數',
                    },
                  ]}
                >
                  <DatePicker.MonthPicker
                    locale={locale}
                    disabledDate={(date) =>
                      date.isBefore(moment().add(1, 'month').endOf('month'))
                    }
                  />
                </Item>
                <Item
                  name="householdNumber"
                  label="戶號"
                  rules={[
                    {
                      required: true,
                      message: '請輸入戶號',
                    },
                  ]}
                >
                  <Select options={householdNumberOptions} />
                </Item>
                <Item
                  name="title"
                  label="明細標題"
                  rules={[
                    {
                      required: true,
                      message: '請輸入明細標題',
                    },
                  ]}
                >
                  <Input />
                </Item>
                <Item
                  name="subjectId"
                  label="明細科目"
                  rules={[
                    {
                      required: true,
                      message: '請選擇明細科目',
                    },
                  ]}
                >
                  <Select options={subjectOptions} />
                </Item>
                {showCreditSubject && (
                  <Item
                    name="creditSubjectId"
                    label="減免科目"
                    rules={[
                      {
                        required: true,
                        message: '請選擇減免科目',
                      },
                    ]}
                  >
                    <Select options={subjectOptions} />
                  </Item>
                )}
                <Item
                  name="money"
                  label="金額"
                  rules={[
                    {
                      required: true,
                      message: '請輸入金額',
                    },
                  ]}
                >
                  <InputNumber />
                </Item>
                <Item name="remark" label="備註">
                  <Input.TextArea />
                </Item>
              </Form>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '10px',
                }}
              >
                <Button
                  type="primary"
                  onClick={() => {
                    form.submit();
                  }}
                >
                  確認
                </Button>
                <Button
                  onClick={() => {
                    setStatus('table');
                    setCurrentData(undefined);
                  }}
                >
                  返回
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Dialog>
      <Button
        onClick={() => {
          setVisible(true);
          setStatus('table');
          handleFinanceDetail(financeId);
        }}
      >
        查看明細
      </Button>
    </>
  );
};

export default FinanceListDialog;
